// Insights in words: a KPI's value with its unit, and how it moved against the month before, said in words so
// that direction never rests on colour or on the chart alone. Which way is better is known only for the KPIs the
// packs name that LAMP measures; for any other the page says higher or lower and leaves the judgement to the Owner.
// Each takes the language to say it in; English when none is given.
import type { Month } from "../api.js";
import { formatNumber, locale } from "../i18n/format.js";
import type { Language } from "../i18n/language.js";
import { CATALOGUES } from "../i18n/words.js";
import type { Page } from "../shell/nav.js";

const number = (value: number, language: Language) => formatNumber(value, language, { maximumFractionDigits: 1 });
const signed = (value: number, language: Language) =>
  formatNumber(value, language, { maximumFractionDigits: 1, signDisplay: "exceptZero" });

/** Rounded to one decimal, as the API rounds for display. */
const oneDecimal = (value: number) => Math.round(value * 10) / 10;

/** `value` with its unit: "52 days", "12.5%", "+1.5 percentage points". */
export function valueWords(value: number, unit: string, language: Language = "en"): string {
  if (unit === "%") return `${number(value, language)}%`;
  if (unit === "percentage points") return CATALOGUES[language].insightsPercentagePoints(signed(value, language));
  return amountOf(value, unit, language);
}

/** An amount of a unit, singular for exactly one: "1 day", "6 days". */
const amountOf = (value: number, unit: string, language: Language): string =>
  CATALOGUES[language].insightsAmount(number(value, language), unit, Math.abs(value) === 1);

/** A month by name: "August", or "Aug" for a chart's labels. */
export function monthName(month: Month, width: "long" | "short" = "long", language: Language = "en"): string {
  const [year = 0, m = 1] = month.split("-").map(Number);
  return new Intl.DateTimeFormat(locale(language), { month: width, timeZone: "UTC" }).format(
    new Date(Date.UTC(year, m - 1, 1)),
  );
}

/** A month with its year, for the tables screen readers read: "August 2026". */
export function monthAndYear(month: Month, language: Language = "en"): string {
  const [year = 0, m = 1] = month.split("-").map(Number);
  return new Intl.DateTimeFormat(locale(language), { month: "long", year: "numeric", timeZone: "UTC" }).format(
    new Date(Date.UTC(year, m - 1, 1)),
  );
}

/** The KPIs LAMP measures whose better way is down; the rest of those it measures are better up. */
const LOWER_IS_BETTER: ReadonlySet<string> = new Set([
  "DSO",
  "overdue %",
  "quote turnaround hours",
  "scrap %",
  "rework %",
  "material cost %",
]);
const HIGHER_IS_BETTER: ReadonlySet<string> = new Set([
  "advance collection %",
  "quote-to-order %",
  "actual vs estimated margin per order",
  "on-time delivery %",
  "OEE",
]);
/** Units of time: a KPI in them that is better lower moved slower or faster. */
const TIME_UNITS: ReadonlySet<string> = new Set(["days", "hours"]);

/**
 * How the latest month moved against the one before, in words: "6 days slower than August". Undefined when only
 * one month was asked.
 */
export function change(
  kpi: {
    readonly name: string;
    readonly unit: string;
    readonly values: readonly { readonly month: Month; readonly value: number | null }[];
  },
  language: Language = "en",
): string | undefined {
  const w = CATALOGUES[language];
  const [before, latest] = kpi.values.slice(-2);
  if (!before || !latest) return undefined;
  const than = monthName(before.month, "long", language);
  if (latest.value === null) return undefined;
  if (before.value === null) return w.insightsNoFigureToCompare(than);
  const delta = oneDecimal(latest.value - before.value);
  if (delta === 0) return w.insightsSameAs(than);
  const up = delta > 0;
  const size = Math.abs(delta);
  const by =
    kpi.unit === "%" || kpi.unit === "percentage points"
      ? w.insightsPoints(number(size, language), size === 1)
      : amountOf(size, kpi.unit, language);
  const lowerBetter = LOWER_IS_BETTER.has(kpi.name);
  if (lowerBetter && TIME_UNITS.has(kpi.unit)) return (up ? w.insightsSlower : w.insightsFaster)(by, than);
  const moved = (up ? w.insightsHigher : w.insightsLower)(by, than);
  if (lowerBetter) return (up ? w.insightsWorse : w.insightsBetter)(moved);
  if (HIGHER_IS_BETTER.has(kpi.name)) return (up ? w.insightsBetter : w.insightsWorse)(moved);
  return moved;
}

/** How each kind of record a contributor may be names itself at the start of its reason. */
const LABELS: Readonly<Record<string, string>> = {
  invoice: "Invoice",
  proforma_invoice: "Proforma invoice",
  payment_receipt: "Payment receipt",
  sales_order: "Sales order",
  enquiry: "Enquiry",
  quotation: "Quotation",
  estimate: "Estimate",
  work_order: "Work order",
  job_card: "Job card",
  stock_issue: "Stock issue",
};
/** Where each kind of record is listed; any other is found among the Runs. */
const LISTED_ON: Readonly<Record<string, Page>> = {
  invoice: "documents",
  proforma_invoice: "documents",
  item: "items",
};

/**
 * Where a contributor with no Run opens its record: the list its kind of record lives on, searched for the number
 * its reason names ("Invoice INV/26-27/0007 fell overdue…"), or the whole list when it names none.
 */
export function recordAt(object: string, reason: string): { readonly page: Page; readonly words: string } {
  const page = LISTED_ON[object] ?? "runs";
  const label = LABELS[object];
  const named = label && reason.startsWith(`${label} `) ? /^[^\s:]+/.exec(reason.slice(label.length + 1)) : null;
  return { page, words: named?.[0] ?? "" };
}
